import { useEffect, useRef, useState } from 'react';
import { Instagram, Play } from 'lucide-react';
import { SITE } from '@/data';
import { Reveal, useEmbedScript } from './Reveal';

/* Real Instagram embeds — the actual reels, playable in place.
   Styled tap-through fallback if Instagram's script is blocked. */
function IgEmbed({ url, index }) {
  useEmbedScript('https://www.instagram.com/embed.js', '__ifbIgJs');
  const [failed, setFailed] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => {
      if (ref.current && !ref.current.querySelector('iframe')) setFailed(true);
    }, 6000);
    return () => clearTimeout(t);
  }, []);

  if (failed) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`reel-fallback-${index + 1}`}
        className="group flex items-center justify-between gap-4 rounded-2xl border border-ivory/10 bg-plum-card p-5 transition-[border-color] duration-300 hover:border-gold/40"
      >
        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-ivory/60">Watch this clip on Instagram</span>
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold text-plum transition-transform duration-300 group-hover:scale-110">
          <Play size={16} fill="currentColor" aria-hidden="true" />
        </span>
      </a>
    );
  }

  return (
    <div
      ref={ref}
      className="flex justify-center overflow-hidden rounded-2xl border border-ivory/10 bg-plum-card [&_blockquote]:!m-0 [&_blockquote]:!min-w-0 [&_blockquote]:!w-full"
      data-testid={`reel-embed-${index + 1}`}
    >
      <blockquote
        className="instagram-media"
        data-instgrm-permalink={url}
        data-instgrm-version="14"
        style={{ margin: 0, width: '100%', minWidth: 0 }}
      >
        <a href={url} target="_blank" rel="noopener noreferrer" className="block p-4 text-[13px] font-bold text-ivory/80">
          Watch this clip on Instagram →
        </a>
      </blockquote>
    </div>
  );
}

export default function Started() {
  return (
    <section id="started" className="mt-24 scroll-mt-24 border-y border-ivory/10 bg-plum-deep py-24 md:mt-32 md:py-28" data-testid="started-section">
      <div className="mx-auto grid max-w-[1280px] grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2">
        <Reveal>
          <div className="flex flex-col items-start gap-5">
            <span className="font-mono text-[13px] font-bold uppercase tracking-[0.25em] text-mauve">The origin story</span>
            <h2 className="font-display text-5xl uppercase leading-[0.95] text-ivory md:text-7xl">
              HOW IT ALL <span className="text-gold">STARTED</span>
            </h2>
            {(() => {
              const [before, after] = SITE.startedStory.split('A million views later,');
              return (
                <p className="max-w-[560px] text-lg leading-relaxed text-ivory/75 md:text-xl">
                  {before}
                  <span className="font-bold text-gold">A million views later</span>
                  {after}
                </p>
              );
            })()}
            <a
              href="https://www.instagram.com/princessnyah/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="started-instagram-link"
              className="mt-2 inline-flex items-center gap-2.5 rounded-full border-2 border-ivory/20 px-6 py-3.5 font-bold text-ivory transition-[border-color,color,transform] duration-200 hover:scale-[1.03] hover:border-mauve hover:text-mauve"
            >
              <Instagram size={18} aria-hidden="true" />
              More on Instagram
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="mx-auto w-full max-w-[380px]">
            <span className="mb-4 block text-center font-mono text-[12px] font-bold uppercase tracking-[0.25em] text-sky">
              Where it began · the first Top 10
            </span>
            <IgEmbed url={SITE.firstTop10Reels[0]} index={0} />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

